import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { FormControl, Icon, Option, Select } from '@amobee/component-library';
import { debounce, every, filter, hasIn, isEmpty, join, map, some, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SelectorAccordion from './SelectorAccordion/SelectorAccordion';
import LocalIcon from '../Icon/Icon';
import './MultiLevelTableSelector.scss';
import { getUpdatedTree } from '../../../utils/MultiLevelTableUtils';

const stringsToObjectValues = (names) => names.map((name, index) => ({ label: name, value: name, index }));

const getJoinedNames = (names) =>
  names.length > 1 ? names.slice(0, names.length - 1).join(', ') + ` and ${names.slice(-1)[0]}` : names[0];

const getPlaceholder = (name, allNames) => {
  const levelName = allNames.length <= 1 ? 'All' : name === 'All' ? getJoinedNames(allNames) : name;
  return `Search ${levelName}`;
};

const getSearchMatchResult = (searchMatchArr, key) => (
  <div key={key}>
    {searchMatchArr.map((item, index) => (
      <span
        className={classNames('search-filtered', {
          'search-match-text': item.match,
        })}
        key={key + index}
      >
        {item.value}
      </span>
    ))}
  </div>
);

// Convert/Prepare string value to array of "matched/not matched" objects with string value for search match styling
const strToSearchMatchArr = (originalStr, searchVal) => {
  let str = originalStr;
  const resultArr = [];
  const getMatchIndex = (currStr) => currStr.toLowerCase().indexOf(searchVal.toLowerCase());

  if (originalStr && getMatchIndex(str) !== -1) {
    let match = true;

    while (match) {
      const matchIdx = getMatchIndex(str);

      if (matchIdx > 0) {
        resultArr.push({
          match: false,
          value: str.slice(0, matchIdx),
        });
        str = str.slice(matchIdx);
      }
      resultArr.push({
        match: true,
        value: str.slice(0, searchVal.length),
      });
      str = str.slice(searchVal.length);
      match = getMatchIndex(str) !== -1;
    }
    if (str.length) {
      resultArr.push({
        match: false,
        value: str,
      });
    }
  } else {
    resultArr.push({
      match: false,
      value: str,
    });
  }
  return resultArr;
};

const areAllChildrenSensitive = (children = []) => children.length && every(children, 'sensitive');

const areSomeChildrenSelected = (children = []) => {
  if (isEmpty(children)) return false;
  const checkedAmount = filter(children, 'selected').length;
  // if no children are selected, check if there are indeterminate children
  return checkedAmount > 0
    ? children.length !== checkedAmount
    : some(children, (child) => areSomeChildrenSelected(child.children));
};

const hasAnySelectedDescendantNode = (item = {}) => {
  if (item.selected) return true;
  if (!item.children?.length) return false;
  return some(item.children, hasAnySelectedDescendantNode);
};

export const SEARCH_SCOPE_ALL = 'all';
export const SEARCH_SCOPE_SERIES = 'series';
export const SEARCH_SCOPE_EPISODES = 'episodes';

const SEARCH_SCOPE_OPTIONS = [
  { value: SEARCH_SCOPE_ALL, label: 'Search All' },
  { value: SEARCH_SCOPE_SERIES, label: 'Series' },
  { value: SEARCH_SCOPE_EPISODES, label: 'Episodes' },
];

const getSearchScopeLabel = (scope) =>
  (SEARCH_SCOPE_OPTIONS.find((option) => option.value === scope) || SEARCH_SCOPE_OPTIONS[0]).label;

const MultiLevelTableSelector = ({
  levelNames,
  tableData,
  onChange,
  listLimit,
  disabled,
  showLevelSelector,
  onScrolledToBottom,
  footerContent,
  isSearchDisabled,
  searchInputValue,
  externalSearchValue,
  onSearchQueryChange,
  minSearchChars,
  emptyContent,
  showSearchScope,
  searchScope,
  onSearchScopeChange,
  showExactMatch,
  exactMatch,
  onExactMatchChange,
  showClearSearch,
  onClearSearch,
  collapseOnParentMatch,
  forceParentIndeterminate,
}) => {
  const controlledSearchInputValue = searchInputValue !== undefined ? searchInputValue : externalSearchValue;
  const controlledSearchQuery = externalSearchValue !== undefined ? externalSearchValue : controlledSearchInputValue;
  const isSearchControlled = controlledSearchInputValue !== undefined;
  // 'All' is a default value added to provided names list
  const allLevelNames = stringsToObjectValues(['All', ...levelNames]);

  const [tableDataValue, setTableDataValue] = useState(tableData);
  const [searchValue, setSearchValue] = useState('');
  const [showMinCharsError, setShowMinCharsError] = useState(false);
  const errorTimerRef = useRef(null);
  // In controlled mode, only treat the value as an active search if it meets the minimum length.
  // This prevents client-side filtering when the parent hasn't issued a real search yet.
  const externalSearchActive =
    isSearchControlled && (controlledSearchQuery || '').length >= minSearchChars ? controlledSearchQuery : '';
  const activeSearchValue = isSearchControlled ? externalSearchActive : searchValue;
  const inputValue = isSearchControlled ? controlledSearchInputValue || '' : searchValue;
  const [levelValue, setLevelValue] = useState(allLevelNames[1]);
  const [selectDataValue, setSelectDataValue] = useState({});
  const [placeholderValue, setPlaceholderValue] = useState(getPlaceholder(levelNames[0], levelNames));
  const [accordionElements, setAccordionElements] = useState([]);
  const [scrollStep, setScrollStep] = useState(1);
  const [isScopePickerOpen, setIsScopePickerOpen] = useState(false);
  const containerRef = useRef();
  const scopePickerRef = useRef(null);
  const showSelectAll = levelNames.length === 1;
  const activeScope = searchScope || SEARCH_SCOPE_ALL;

  useEffect(() => {
    if (!isScopePickerOpen) return undefined;
    const handleDocumentMouseDown = (event) => {
      if (scopePickerRef.current && !scopePickerRef.current.contains(event.target)) {
        setIsScopePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [isScopePickerOpen]);

  const handleScopeOptionClick = (option) => {
    if (onSearchScopeChange) onSearchScopeChange(option.value);
    setIsScopePickerOpen(false);
  };

  const handleExactMatchToggle = (nextValue) => {
    if (onExactMatchChange) onExactMatchChange(nextValue);
  };

  const handleClearSearchClick = () => {
    if (onClearSearch) {
      onClearSearch();
    } else if (isSearchControlled) {
      if (onSearchQueryChange) onSearchQueryChange('');
    } else {
      setSearchValue('');
    }
  };

  // `source` disambiguates rows that share the same id+label but come from different data
  // providers (e.g. unified TV shows both a hisense and a peerlogix copy of the same show).
  const getUniqKey = (data) =>
    join([...(data.parent_ids || []), ...(data.source ? [data.source] : []), data.value, data.label], ',');

  const populateAccordion = (items, searchText, levelName = levelValue.value, increaseLimit = false) => {
    // In controlled (API-driven) mode the caller already provides exactly the right set of
    // items for this page — don't apply an internal row cap so we never truncate mid-series.
    const rowToRender = isSearchControlled ? Infinity : increaseLimit ? scrollStep * listLimit : listLimit;
    if (increaseLimit) {
      setScrollStep(scrollStep + 1);
    }
    const indexOfLevelToShow = levelNames.indexOf(levelName);

    function getRecursionAccordionElements(dataArray, parentName = '', level = 0, isParentDisplayed = false) {
      let elements = [];

      // Preserve original order to ensure virtual scroller appends only
      dataArray.forEach((data) => {
        let innerChildren = [];
        let searchMatchResult = '';
        const isSearchAll = levelName === 'All' && searchText;
        const secondaryText = isParentDisplayed ? '' : parentName ? `${parentName}, ${data.label}` : data.label;
        let isShown = false;

        if (searchText && (isSearchControlled || level === indexOfLevelToShow || levelName === 'All')) {
          // Provide a search match array in next structure (consider 'se' to be search text and 'Search' the original text):
          // [{ match: true, value: 'Se' }, { match: false, value: 'arch' }]
          const searchMatchArr = strToSearchMatchArr(data.label, searchText);
          if (some(searchMatchArr, 'match')) {
            searchMatchResult = getSearchMatchResult(searchMatchArr, getUniqKey(data));
          }
        }
        if (!isEmpty(data.children)) {
          // Process the children data to include as html elements to parent element.
          // In controlled mode all nodes are pre-filtered by backend — treat every parent as displayed.
          const isCurrentDisplayed =
            isParentDisplayed ||
            isSearchControlled ||
            (!searchText && level >= indexOfLevelToShow) ||
            (!!searchMatchResult && levelName !== 'All');
          innerChildren = getRecursionAccordionElements(data.children, secondaryText, level + 1, isCurrentDisplayed);
        }
        // Show element if it has search matches or no search text provided.
        // When isSearchControlled the backend already pre-filtered results — show all nodes.
        if (
          (isParentDisplayed || searchMatchResult || !searchText || isSearchControlled) &&
          level >= indexOfLevelToShow &&
          elements.length <= rowToRender
        ) {
          const hasToggle = !isSearchAll && !!data.children;
          const indeterminate = areSomeChildrenSelected(data.children);
          const showWarning = data.sensitive || areAllChildrenSensitive(data.children);
          const rightContent = showWarning ? <Icon name="StatusWarning" color="#CAA900" /> : '';
          isShown = true;

          // In partial-tree mode (search scope = episodes/all), a parent's "selected" state always
          // represents only the visible children — never the entire branch — so render it as
          // indeterminate while any descendant is selected, and route clicks to "deselect all".
          const isPartialParent =
            forceParentIndeterminate && !!data.children?.length && hasAnySelectedDescendantNode(data);
          const renderedIsChecked = isPartialParent ? false : data.selected;
          const renderedIsIndeterminate = isPartialParent ? true : indeterminate;
          const handleCheck = isPartialParent
            ? () => setSelectDataValue({ ...data, selected: false })
            : (checked) => setSelectDataValue({ ...data, selected: checked });

          const nodeKey = getUniqKey(data);
          // Default-open any node that has visible children during a controlled search.
          // When collapseOnParentMatch is enabled, top-level nodes whose own label matched
          // stay collapsed even if children also matched — per the design, a parent match
          // implies "select the whole series", so episode matches inside it are noise.
          const isParentSelfMatch = collapseOnParentMatch && level === 0 && !!searchMatchResult;
          const shouldDefaultOpen =
            isSearchControlled && !!searchText && innerChildren.length > 0 && !isParentSelfMatch;
          const instanceKey = shouldDefaultOpen ? nodeKey + '__open' : nodeKey;
          const newElement = (
            <SelectorAccordion
              label={searchMatchResult || data.label}
              key={instanceKey}
              isChecked={renderedIsChecked}
              isCheckboxIndeterminate={renderedIsIndeterminate}
              onCheck={handleCheck}
              hasToggle={hasToggle}
              defaultOpen={shouldDefaultOpen}
              content={innerChildren}
              secondaryText={isParentDisplayed ? '' : parentName}
              rightContent={rightContent}
              hasExtraMargin={showSelectAll}
              leadingAdornment={data.leadingAdornment}
            />
          );
          // Display episode without season directly under show - PHOB-21
          const childElement = data.isSeason !== undefined && !data.isSeason ? innerChildren : newElement;
          // Append-only to preserve stable positions for virtualization
          elements.push(childElement);
        }
        // In controlled search mode children are already nested inside the accordion content — don't also flatten them.
        if ((!isShown || isSearchAll) && !isSearchControlled) {
          elements = [...elements, ...innerChildren];
        }
      });

      return elements;
    }

    // In controlled mode show all provided items; in self-managed mode apply the row cap.
    const itemsToRender = isSearchControlled || searchText ? items : items.slice(0, rowToRender);
    const result = getRecursionAccordionElements(itemsToRender);
    setAccordionElements(isSearchControlled ? result : result.slice(0, rowToRender));
  };

  const onSelectChange = (e) => {
    const value = hasIn(e, 'target.value') ? e.target.value : e.value || e;
    setPlaceholderValue(getPlaceholder(value, levelNames));
    setLevelValue(value);
    setSearchValue('');
  };

  const handleScroll = () => {
    const container = containerRef.current;
    const { scrollTop, clientHeight, scrollHeight } = container;
    const ratio = (scrollTop + clientHeight) / scrollHeight;
    if (ratio >= 0.95) {
      if (tableDataValue.length > listLimit * scrollStep) {
        populateAccordion(tableDataValue, activeSearchValue, levelValue.value || levelValue, true);
      }
      if (onScrolledToBottom) {
        onScrolledToBottom();
      }
    }
  };

  const populateAccordionRef = useRef(populateAccordion);
  populateAccordionRef.current = populateAccordion;

  const searchTreeDebounce = useRef(debounce((...args) => populateAccordionRef.current(...args), 200)).current;

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);

  const onSearchChange = (e) => {
    const val = e?.target?.value ?? e?.value ?? '';

    clearErrorTimer();
    if (val.length === 0 || val.length >= minSearchChars) {
      setShowMinCharsError(false);
    } else {
      errorTimerRef.current = setTimeout(() => setShowMinCharsError(true), 3000);
    }

    if (isSearchControlled) {
      if (onSearchQueryChange) onSearchQueryChange(val);
    } else {
      setSearchValue(val);
    }
  };

  const onSearchBlur = () => {
    const val = inputValue;
    if (val.length > 0 && val.length < minSearchChars) {
      clearErrorTimer();
      setShowMinCharsError(true);
    }
  };

  useLayoutEffect(() => {
    populateAccordion(tableDataValue, activeSearchValue, levelValue.value || levelValue);
  }, [levelValue, tableDataValue, activeSearchValue]);
  useEffect(() => {
    if (!isSearchControlled) {
      searchTreeDebounce(tableDataValue, searchValue, levelValue.value || levelValue);
    }
  }, [searchValue]);

  useEffect(() => setTableDataValue(tableData), [tableData]);

  useEffect(() => {
    if (isSearchControlled && inputValue.length === 0) {
      setShowMinCharsError(false);
      clearErrorTimer();
    }
  }, [inputValue]);

  useEffect(
    () => () => {
      clearErrorTimer();
      searchTreeDebounce.cancel();
    },
    [clearErrorTimer, searchTreeDebounce]
  );

  useEffect(() => {
    if (!isEmpty(selectDataValue)) {
      let updatedData = [];
      if (selectDataValue.isSelectAll) {
        updatedData = map(tableDataValue, (item) => ({ ...item, selected: selectDataValue.selected }));
      } else {
        updatedData = getUpdatedTree(tableDataValue, selectDataValue.selected, selectDataValue);
      }
      setTableDataValue([...updatedData]);
      // Provide updated data to onChange handler
      onChange(updatedData, selectDataValue);
    }
  }, [selectDataValue]);

  return (
    <div className={classNames('selector-wrapper', { disabled, 'search-error': showMinCharsError })}>
      <div className="selector-header">
        <Icon name="Search" className="search-icon" />
        {showSearchScope && (
          <div className={classNames('search-scope', { open: isScopePickerOpen })} ref={scopePickerRef}>
            <div
              className="search-scope-trigger"
              onClick={() => !isSearchDisabled && setIsScopePickerOpen((open) => !open)}
            >
              <span className="search-scope-label">{getSearchScopeLabel(activeScope)}</span>
              <LocalIcon
                iconId="chevron-up"
                className={classNames('search-scope-chevron', isScopePickerOpen ? 'up' : 'down')}
              />
            </div>
            {isScopePickerOpen && (
              <div className="search-scope-menu">
                {SEARCH_SCOPE_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={classNames('search-scope-option', { selected: option.value === activeScope })}
                    onClick={() => handleScopeOptionClick(option)}
                  >
                    <span className="search-scope-option-label">{option.label}</span>
                    {option.value === activeScope && <Icon name="Check" className="search-scope-option-check" />}
                  </div>
                ))}
                {showExactMatch && (
                  <div
                    className={classNames('search-scope-exact-match', { on: !!exactMatch })}
                    onClick={() => handleExactMatchToggle(!exactMatch)}
                  >
                    <span className="exact-match-track">
                      <span className="exact-match-thumb" />
                    </span>
                    <span className="exact-match-label">Exact Match</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <FormControl className="search-control">
          {showLevelSelector && levelNames.length > 1 && (
            <Select initialValue={levelValue} onChange={onSelectChange} irreversible>
              {allLevelNames.map(({ value, label }) => (
                <Option key={value} label={label} value={value} />
              ))}
            </Select>
          )}
          <input
            className="selector-search"
            placeholder={placeholderValue}
            value={inputValue}
            onChange={onSearchChange}
            onBlur={onSearchBlur}
            disabled={isSearchDisabled}
          />
        </FormControl>
        {showClearSearch && inputValue && (
          <span className="search-clear" onClick={handleClearSearchClick}>
            <LocalIcon iconId="x" width="10px" height="10px" />
          </span>
        )}
      </div>
      {showMinCharsError && <div className="search-min-chars-error">Enter 2 or more characters to see results</div>}
      <div className="selector-body" ref={containerRef} onScroll={handleScroll}>
        {!tableData.length && (emptyContent || <h4 className="no-content-message">No content to display.</h4>)}
        {showSelectAll && (
          <SelectorAccordion
            label={levelNames[0]}
            key={levelNames[0]}
            isChecked={every(tableDataValue, 'selected')}
            onCheck={(selected) => setSelectDataValue({ isSelectAll: true, selected })}
            hasToggle={false}
            isSelectAll
          />
        )}
        {accordionElements}
        {footerContent && <div className="selector-footer">{footerContent}</div>}
      </div>
    </div>
  );
};

MultiLevelTableSelector.propTypes = {
  levelNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  tableData: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  listLimit: PropTypes.number,
  disabled: PropTypes.bool,
  showLevelSelector: PropTypes.bool,
  onScrolledToBottom: PropTypes.func,
  footerContent: PropTypes.node,
  isSearchDisabled: PropTypes.bool,
  searchInputValue: PropTypes.string,
  externalSearchValue: PropTypes.string,
  onSearchQueryChange: PropTypes.func,
  minSearchChars: PropTypes.number,
  emptyContent: PropTypes.node,
  showSearchScope: PropTypes.bool,
  searchScope: PropTypes.oneOf([SEARCH_SCOPE_ALL, SEARCH_SCOPE_SERIES, SEARCH_SCOPE_EPISODES]),
  onSearchScopeChange: PropTypes.func,
  showExactMatch: PropTypes.bool,
  exactMatch: PropTypes.bool,
  onExactMatchChange: PropTypes.func,
  showClearSearch: PropTypes.bool,
  onClearSearch: PropTypes.func,
  collapseOnParentMatch: PropTypes.bool,
  forceParentIndeterminate: PropTypes.bool,
};

MultiLevelTableSelector.defaultProps = {
  levelNames: [],
  tableData: [],
  listLimit: 100,
  disabled: false,
  showLevelSelector: true,
  onScrolledToBottom: null,
  footerContent: null,
  isSearchDisabled: false,
  searchInputValue: undefined,
  externalSearchValue: undefined,
  onSearchQueryChange: null,
  minSearchChars: 1,
  emptyContent: null,
  showSearchScope: false,
  searchScope: SEARCH_SCOPE_ALL,
  onSearchScopeChange: null,
  showExactMatch: false,
  exactMatch: false,
  onExactMatchChange: null,
  showClearSearch: false,
  onClearSearch: null,
  collapseOnParentMatch: false,
  forceParentIndeterminate: false,
};

export default MultiLevelTableSelector;
